import {
  Types,
  withSystem,
  Plugins,
  getSystem,
  type Type,
  Expressions,
} from '@packages/systems/wfdl';
import {
  TTypeApplication,
  TTypeConstructor,
  TRowEmpty,
  constructRow,
  EElement,
} from '@packages/systems/wfdl-ast';

import {
  typeName,
  qualify,
  QualifiedMap,
} from '@packages/systems/wfdl-Qualified';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {type NodePojo} from '@packages/systems/core/nodepojo';
import {reifyElementTree} from '@packages/systems/core/render';
import {nodePojoToElement} from '@packages/systems/core/component-conversions';

import * as DomTypeUtils from '@packages/systems/core/utils/DomTypeUtils';
import {Tree} from '@packages/systems/tree';

import * as JsonDomUtils from '@packages/systems/core/utils/JsonDomUtils';
import type {ReportType, NodePatch, Context} from '../types';
import {type EnhancedExtraContext} from '../ExtraContext';

const tyConRecord = TTypeConstructor(
  qualify(Plugins.name('Builtin'), typeName('Record'))
);

const SYM_OVERRIDES_PATH: {
  in: 'Record';
  at: string;
}[] = [
  {in: 'Record', at: 'sym'},
  {in: 'Record', at: 'overrides'},
];

type PatchCallback = (jsonNodeTree: Array<JSONNode>) => Array<JSONNode>;

type DefaultValueOwner = 'dataType' | 'instDataType';

type TypeCallback = (type: Type) => Type;

type FieldTypeCallback = (type: Type, fieldKey: string) => Type;

const reportMalformedListDefaultValue = (
  reportWarning: (warning: string) => void,
  nodeId: string,
  defaultValueOwner: DefaultValueOwner,
  fieldKey: string,
  reason = 'expected an Array<NodePojo>'
) => {
  const fieldDescription = fieldKey ? ` field "${fieldKey}"` : '';

  reportWarning(
    `Skipped malformed List<Element> defaultValue on node "${nodeId}" (${defaultValueOwner}${fieldDescription}); ${reason}.`
  );
};

const createMalformedDefaultValueReporter =
  (
    reportWarning: (msg: string, warning: string) => void,
    msg = 'MalformedDefaultValue'
  ) =>
  (warning: string) =>
    reportWarning(msg, warning);

const patchDefaultValueSubtrees =
  (
    jsonNode: JSONNode,
    plugins: Plugins.PluginsType,
    cb: PatchCallback,
    reportMalformedDefaultValue: (warning: string) => void,
    defaultValueOwner: DefaultValueOwner
  ) =>
  (type: Type, fieldKey: string) =>
    _targetElementDefaults(type, plugins, cb, (reason) =>
      reportMalformedListDefaultValue(
        reportMalformedDefaultValue,
        jsonNode._id,
        defaultValueOwner,
        fieldKey,
        reason
      )
    );

/**
 * Takes an array of `defaultValue`s and invokes a callback on each `defaultValue`:
 * 1. We start with a humble NodePojo 💩 🚽
 * 2. -> Element
 * 3. -> JSONNode (apply migration callback)
 * 4. -> Element
 * 5. -> Back to an NodePojo :sweat_smile:
 */
const _updateDefaultValueSubtree = (
  defaultValue: Array<NodePojo>,
  plugins: Plugins.PluginsType,
  cb: PatchCallback
): Array<NodePojo> => {
  const tree = new Tree(Plugins.getPluginComponents(plugins));

  return defaultValue.reduce<Array<NodePojo>>((acc, curr) => {
    // Convert NodePojo -> Element
    const asElement = nodePojoToElement(plugins)(
      Plugins.getPluginDataTypes(plugins)
    )(curr);

    // Convert Element -> JSONNodes
    const jsonNodes = asElement ? tree.getElementAsJSONNodes(asElement) : [];

    // Update defaultValue and convert JSONNode -> Element (reification)
    const elementTree = reifyElementTree(
      cb(jsonNodes),
      QualifiedMap.empty(),
      plugins
    );

    const evalEnv = Plugins.getEvalEnv(plugins);

    // Convert Element -> NodePojo
    for (const e of elementTree) {
      const nodePojo = Expressions.evaluate(evalEnv, EElement(e));

      acc.push(nodePojo);
    }

    return acc;
  }, []);
};

/**
 * Takes a Record and mutates defaultValue for each field and
 * returns a new updated Record.
 */
const _updateDefaultValueFields = (
  dataType: Type,
  cb: FieldTypeCallback
): {
  [key: string]: Type;
} | null => {
  const fields = Types.getRecordFields(dataType);

  if (!fields) return null;

  const newFields: Record<string, any> = {};

  for (const fieldKey in fields) {
    const type = fields[fieldKey];

    // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
    newFields[fieldKey] = cb(type, fieldKey);
  }

  return newFields;
};

/**
 * User Component Root: Patches `dataType` defaults
 *
 * symbol.dataType.{fields}
 * - TList<Element>
 *  - defaultValue: [NodePojo, NodePojo, etc...]
 */
const _patchUserComponentRootDefaultValues = (
  jsonNode: JSONNode,
  cb: FieldTypeCallback
) => {
  if (jsonNode && jsonNode.dataType) {
    // Some sites have persisted `dataType`s that are serialized and some that are objects.
    const dataType =
      typeof jsonNode.dataType === 'string'
        ? JSON.parse(jsonNode.dataType)
        : jsonNode.dataType;

    const updatedFields = _updateDefaultValueFields(dataType, cb);

    if (updatedFields) {
      const updatedDataType = TTypeApplication({
        con: tyConRecord,
        arg: constructRow({items: updatedFields, tail: TRowEmpty}),
      });

      jsonNode.dataType = JSON.stringify(updatedDataType);
    }
  }
};

/**
 * User Component Instance: Patches `instDataType`
 *
 * node.data.sym.instDataType.sym.overrides.{fields}
 * - TList<Element>
 *  - defaultValue: [NodePojo, NodePojo, etc...]
 */
const _patchUserComponentInstDefaultValues = (
  jsonNode: JSONNode,
  cb: FieldTypeCallback
) => {
  const instDataTypeRaw = jsonNode?.data?.sym?.instDataType;

  // Update Defaults
  if (instDataTypeRaw) {
    // Some sites have persisted `dataType`s that are serialized and some that are objects.
    const instDataType: Type =
      typeof instDataTypeRaw === 'string'
        ? JSON.parse(instDataTypeRaw)
        : instDataTypeRaw;

    const userProvidedDefaults = Types.__DEPRECATED__getTypeAtExpressionPath(
      instDataType,
      SYM_OVERRIDES_PATH
    );

    // Update User Provided Defaults at the instance level (i.g: on instDataType)
    if (userProvidedDefaults) {
      const updatedFields = _updateDefaultValueFields(userProvidedDefaults, cb);

      if (updatedFields) {
        let updatedDataType = TTypeApplication({
          con: tyConRecord,
          arg: constructRow({items: updatedFields, tail: TRowEmpty}),
        });

        // Set the newly updated fields on `node.data.sym.instDataType.sym.overrides`
        updatedDataType = Types.setTypeAtBindingPath(
          instDataType,
          SYM_OVERRIDES_PATH,
          updatedDataType
        );

        if (jsonNode.data) {
          jsonNode.data.sym.instDataType = JSON.stringify(updatedDataType);
        }
      }
    }
  }
};

/**
 * Maps a JSONNode to a patch algorithm depending on if it's a Component root
 * or inst and applies a callback.
 * i.e: root or inst
 */
const _scanDefaultValues = (jsonNode: JSONNode, cb: TypeCallback): void => {
  const isSymbolRoot = DomTypeUtils.isSymbolRoot(jsonNode.data);
  const isSymbolInst =
    jsonNode.type === 'Symbol' && DomTypeUtils.isSymbolInst(jsonNode.data);

  // Symbol roots and instances store default values in different places:
  // roots use `dataType`, while instances use `data.sym.instDataType.sym.overrides`.
  if (isSymbolRoot) {
    _patchUserComponentRootDefaultValues(jsonNode, (type) => cb(type));
  } else if (isSymbolInst) {
    _patchUserComponentInstDefaultValues(jsonNode, (type) => cb(type));
  }
};

const isValidNodePojo = (obj: unknown): obj is NodePojo => {
  if (!obj || typeof obj !== 'object') return false;

  return (
    'id' in obj &&
    'type' in obj &&
    'data' in obj &&
    'children' in obj &&
    'text' in obj &&
    typeof obj.id === 'string'
  );
};
const _targetElementDefaults = (
  type: Type,
  plugins: Plugins.PluginsType,
  cb: PatchCallback,
  reportMalformedDefaultValue: (reason: string) => void
): Type => {
  const system = getSystem(type);

  if (!system?.defaultValue || !Types.isListOfElements(type)) {
    return type;
  }

  // Only patch `defaultValue`s that are type RichTextChildren or List<Element>,
  // these are `defaultValue`s that are Array<NodePojo>.
  // Some persisted sites have invalid List<Element> defaults like `{}`.
  // Skip them here so unrelated patches cannot crash while traversing defaults.
  if (!Array.isArray(system.defaultValue)) {
    reportMalformedDefaultValue('expected an Array<NodePojo>');
    return type;
  }

  // Guard against arrays that contain non-NodePojo items (e.g. null, primitives,
  // or empty objects). Such entries would crash conversion downstream.
  if (!system.defaultValue.every(isValidNodePojo)) {
    reportMalformedDefaultValue('array contains non-NodePojo items');
    return type;
  }

  system.defaultValue = _updateDefaultValueSubtree(
    system.defaultValue,
    plugins,
    cb
  );

  return withSystem(system, type);
};

// Refines the target type for the callback to only Elements
const _scanDefaultValueSubtrees = (
  jsonNode: JSONNode,
  plugins: Plugins.PluginsType,
  cb: PatchCallback,
  reportMalformedDefaultValue: (warning: string) => void
) => {
  if (DomTypeUtils.isSymbolRoot(jsonNode.data)) {
    _patchUserComponentRootDefaultValues(
      jsonNode,
      patchDefaultValueSubtrees(
        jsonNode,
        plugins,
        cb,
        reportMalformedDefaultValue,
        'dataType'
      )
    );
    return;
  }

  if (jsonNode.type !== 'Symbol' || !DomTypeUtils.isSymbolInst(jsonNode.data)) {
    return;
  }

  _patchUserComponentInstDefaultValues(
    jsonNode,
    patchDefaultValueSubtrees(
      jsonNode,
      plugins,
      cb,
      reportMalformedDefaultValue,
      'instDataType'
    )
  );
};

/**
 * Takes a JSONNode and invokes `_scanDefaultValueSubtrees` to visit and apply
 * `patchToApply` on its defaultValues.
 */
export const applyPatchToDefaultValues = (
  jsonNode: JSONNode,
  patchToApply: NodePatch,
  extraFun: {
    reportWarning: (msg: string, warning: string) => void;
  },
  extraContext: EnhancedExtraContext,
  reportChange?: (arg1: ReportType) => void
): void => {
  if (!extraContext.plugins) return;

  _scanDefaultValueSubtrees(
    jsonNode,
    extraContext.plugins,
    (defaultValueNodeTree) =>
      JsonDomUtils.processNodes(defaultValueNodeTree, (context: Context) => {
        const {node} = context;
        context.extraContext = extraContext;

        // It's possible we can turn this into a unary function because `extraContext` is already on
        // `context` and includes `reportWarning`.
        const {needed, apply, logData} = patchToApply(context, extraFun);

        if (needed) {
          const newSubtree = apply() || [node.jsonNode];
          node.newSubtree = newSubtree;
          // @ts-expect-error when we're removing a jsonNode, it becomes undefined.
          node.jsonNode = newSubtree.length ? newSubtree[0] : undefined;

          if (reportChange && logData.meta) {
            logData.meta.defaultValue = true;
            reportChange(logData);
          }
        }

        return node.newSubtree;
      }),
    createMalformedDefaultValueReporter(extraFun.reportWarning)
  );
};

export const detectRegressionsDefaultValues = (
  jsonNode: JSONNode,
  patchToApply: NodePatch,
  extraFun: {
    reportWarning: (msg: string, warning: string) => void;
  },
  extraContext: EnhancedExtraContext,
  reportChange?: (arg1: ReportType) => void
): void => {
  if (!extraContext.plugins) return;

  _scanDefaultValueSubtrees(
    jsonNode,
    extraContext.plugins,
    (defaultValueNodeTree) =>
      JsonDomUtils.processNodes(defaultValueNodeTree, (context: Context) => {
        context.extraContext = extraContext;

        // It's possible we can turn this into a unary function because `extraContext` is already on
        // `context` and includes `reportWarning`.
        const {needed, logData} = patchToApply(context, extraFun);

        if (needed && reportChange && logData.meta) {
          logData.meta.defaultValue = true;
          reportChange(logData);
        }
      }),
    createMalformedDefaultValueReporter(extraFun.reportWarning)
  );
};

export const testUtils = {
  _scanDefaultValueSubtrees,
} as const;

// Export this util to a patch if it needs to update the raw dataTypes
// of either a component root dataType, or the `instDataType` of a
// component instance
//
// NOTE! This util can and should mutate the `dataType` or `data.sym.instDataType`
// of a given jsonNode.
export const applyComponentTypePatch = (
  jsonNode: JSONNode,
  cb: TypeCallback
): void => {
  _scanDefaultValues(jsonNode, cb);
};

// Export this util to determine if you need to use `applyDefaultValuePatchWithin`
//
// NOTE! This util can but should not mutate the `dataType` or `data.sym.instDataType`
// of a given jsonNode.
export const needsComponentTypePatch = (
  jsonNode: JSONNode,
  cb: (arg1: Type) => boolean
): boolean => {
  let needed = false;
  _scanDefaultValues(jsonNode, (type) => {
    // The callback for determining if there is a need for a patch run
    if (!needed) {
      needed = cb(type);
    }
    // Don't do anything with the type
    return type;
  });
  return needed;
};
